// src/containers/reportBuilder/PreferencesTab.jsx
//
// Report & chart preferences for a selected organisation.
// Ported from takso-poc/takso-reporting-ui/src/pages/preferences/PreferencesPage.jsx.
//
// Only functional when an org is selected — shows a prompt otherwise.
// Word template upload is intentionally excluded (handled separately).
//
// Two sections (tabs):
//   Document — typeface, cover page, header/footer, report sections
//   Charts   — chart display preferences
//
// Two-layer system: platform defaults + org-level overrides (delta), or
// per-activity overrides when exactly one activity is selected.
// Blue = customised; "reset" reverts a field to the platform default.
//
// API:
//   GET  /report-builder/preferences?orgId=            (omit orgId for global settings)
//   GET  /report-builder/preferences?activityIds=      (single activity's own overrides)
//   PUT  /report-builder/preferences?orgId=             (omit orgId for global settings)
//   PUT  /report-builder/preferences?activityIds=       (1 id → saved against that activity;
//                                                          2+ ids → silently ignored for now)

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { reportBuilderGet, reportBuilderPut } from '../../lib/chartApi';

// ── Deep merge (defaults + overrides → merged prefs) ──────────────────────────

function deepMerge(base, override) {
  const result = { ...base };
  for (const key of Object.keys(override || {})) {
    if (
      override[key] !== null &&
      typeof override[key] === 'object' &&
      !Array.isArray(override[key]) &&
      typeof base[key] === 'object' &&
      base[key] !== null
    ) {
      result[key] = deepMerge(base[key], override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
}

// ── Nested-object path helpers ─────────────────────────────────────────────────

function getPath(obj, dotPath) {
  return dotPath
    .split('.')
    .reduce((cur, k) => (cur == null ? undefined : cur[k]), obj);
}

function setPath(obj, dotPath, value) {
  const keys = dotPath.split('.');
  const result = { ...obj };
  let node = result;
  for (let i = 0; i < keys.length - 1; i++) {
    node[keys[i]] = { ...(node[keys[i]] || {}) };
    node = node[keys[i]];
  }
  node[keys[keys.length - 1]] = value;
  return result;
}

function deletePath(obj, dotPath) {
  const clone = structuredClone(obj);
  const keys = dotPath.split('.');
  let node = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    if (node[keys[i]] == null) return clone;
    node = node[keys[i]];
  }
  delete node[keys[keys.length - 1]];
  return clone;
}

// ── Toggle switch ──────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, overridden, disabled }) {
  return (
    <label className="pref-toggle pref-toggle--custom">
      <input
        type="checkbox"
        className="pref-toggle__input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span
        className={`pref-toggle__track${
          checked ? ' pref-toggle__track--on' : ''
        }`}
      >
        <span
          className={`pref-toggle__thumb${
            checked ? ' pref-toggle__thumb--on' : ''
          }`}
        />
      </span>
    </label>
  );
}

// ── Section heading ────────────────────────────────────────────────────────────

function SectionHeading({ children }) {
  return <div className="pref-section-heading">{children}</div>;
}

// ── ToggleRow — one preference item with a toggle ──────────────────────────────

function ToggleRow({
  label,
  hint,
  path,
  defaults,
  overrides,
  onChange,
  onClear,
  disabled,
  disabledNote,
}) {
  const { t } = useTranslation();
  const defaultVal = getPath(defaults, path);
  const overrideVal = getPath(overrides, path);
  const hasOverride = overrideVal !== undefined && overrideVal !== null;
  const isCustom = hasOverride && overrideVal !== defaultVal;
  const value = hasOverride ? overrideVal : defaultVal;

  return (
    <div
      className={`pref-row pref-row--toggle${
        disabled ? ' pref-row--disabled' : ''
      }`}
    >
      <Toggle
        checked={Boolean(value)}
        onChange={(v) => onChange(path, v)}
        overridden={isCustom}
        disabled={disabled}
      />
      <div className="pref-row__body">
        <div className="pref-row__label">{label}</div>
        {disabled && disabledNote ? (
          <div className="pref-row__hint pref-row__hint--disabled">
            {disabledNote}
          </div>
        ) : (
          hint && <div className="pref-row__hint">{hint}</div>
        )}
      </div>
      {!disabled && isCustom && (
        <button
          className="pref-row__reset"
          onClick={() => onClear(path)}
          title={t('report.builder.preferences.revert_to_platform_default')}
        >
          {t('actions.reset').toLowerCase()}
        </button>
      )}
      {!disabled && (
        <div className="pref-row__status">
          {isCustom ? (
            <span className="pref-row__status--custom">
              {t('common.custom').toLowerCase()}
            </span>
          ) : (
            <span className="pref-row__status--default">
              {t('common.default').toLowerCase()}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── TextField ─────────────────────────────────────────────────────────────────

function TextField({
  label,
  hint,
  path,
  defaults,
  overrides,
  onChange,
  onClear,
}) {
  const { t } = useTranslation();
  const defaultVal = getPath(defaults, path);
  const overrideVal = getPath(overrides, path);
  const hasOverride =
    overrideVal !== undefined && overrideVal !== null && overrideVal !== '';

  return (
    <div className="pref-row pref-row--field">
      <div className="pref-row__meta">
        <div className="pref-row__label">{label}</div>
        {hint && <div className="pref-row__hint">{hint}</div>}
      </div>
      <div className="pref-row__control">
        <input
          type="text"
          className={`pref-input${hasOverride ? ' pref-input--custom' : ''}`}
          value={hasOverride ? overrideVal : ''}
          placeholder={defaultVal || `(${t('common.none').toLowerCase()})`}
          onChange={(e) => onChange(path, e.target.value)}
        />
        {hasOverride && (
          <button
            className="pref-row__clear"
            onClick={() => onClear(path)}
            title={t('report.builder.preferences.revert_to_platform_default')}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

// ── SelectField ───────────────────────────────────────────────────────────────

function SelectField({
  label,
  hint,
  path,
  defaults,
  overrides,
  onChange,
  onClear,
  options,
  disabled,
}) {
  const { t } = useTranslation();
  const defaultVal = getPath(defaults, path);
  const overrideVal = getPath(overrides, path);
  const hasOverride = overrideVal !== undefined && overrideVal !== null;
  const isCustom = hasOverride && overrideVal !== defaultVal;
  const value = hasOverride ? overrideVal : defaultVal || '';

  return (
    <div
      className={`pref-row pref-row--field${
        disabled ? ' pref-row--disabled' : ''
      }`}
    >
      <div className="pref-row__meta">
        <div className="pref-row__label">{label}</div>
        {hint && <div className="pref-row__hint">{hint}</div>}
      </div>
      <div className="pref-row__control">
        <select
          className={`pref-select${
            isCustom && !disabled ? ' pref-select--custom' : ''
          }`}
          value={value}
          disabled={disabled}
          onChange={(e) => !disabled && onChange(path, e.target.value)}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {!disabled && isCustom && (
          <button
            className="pref-row__clear"
            onClick={() => onClear(path)}
            title={t('report.builder.preferences.revert_to_platform_default')}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

// ── Platform defaults (used when backend returns nothing) ──────────────────────
// These mirror the `?? true` / `?? false` fallbacks in the preview components so
// the toggles always show the correct starting state even before the API is built.

const PLATFORM_DEFAULTS = {
  document: {
    fontFamily: 'Arial',
    bodyFontSize: '11',
    chartTitleStyle: 'Heading 4',
    cover: { enabled: true },
    header: { enabled: true, leftText: '', rightText: '' },
    footer: { enabled: true, centreText: '', showPageNumbers: true },
    sections: {
      corporateStatement: true,
      endSignature: true,
      tableOfContents: true,
      executiveSummary: true,
      strategicAlignment: true,
      intendedImpact: true,
      evaluation: true,
      conclusions: true,
      appendices: true,
      surveyResponsesAppendix: true,
      glossary: true,
    },
  },
  charts: {
    outcomes: { useSurveyStats: false },
  },
  // Outcome measure display in the report template
  outcomes: {
    useGraded: false, // false = Full outcome measure (default), true = Graded
  },
  // Banner and spot colour — applied to table column headings and section
  // accent elements throughout the report. Default is white (no colour).
  bannerColour: {
    enabled: false,
    colour: '#ffffff',
  },
  // Cover image background colour — the letterbox colour behind the cover
  // photo (visible in any gap where the image doesn't fully fill the frame,
  // and while the image is loading). Platform default is dark grey; an org
  // override of `colour: null` means "no colour" — a transparent letterbox
  // with no overlay tint.
  coverImageBackground: {
    colour: '#1f2937',
  },
};

// ── Main component ─────────────────────────────────────────────────────────────

const FONTS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Calibri', label: 'Calibri' },
  { value: 'Garamond', label: 'Garamond' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
];

const SIZES = [9, 10, 11, 12, 13, 14].map((n) => ({
  value: String(n),
  label: `${n} pt`,
}));

const HEADING_STYLES = [
  { value: 'Heading 2', label: 'Heading 2 — large section heading' },
  { value: 'Heading 3', label: 'Heading 3 — sub-section heading' },
  { value: 'Heading 4', label: 'Heading 4 — chart label (default)' },
  { value: 'Heading 5', label: 'Heading 5 — small label' },
];

export default function PreferencesTab({
  orgId,
  orgName,
  activityId,
  activityIds,
  onPrefsChange,
}) {
  // Preferences are per-activity when exactly one activity is selected;
  // otherwise (0 or 2+) they fall back to the org-level default — see
  // ReportBuilder.getPreferences/putPreferences on the backend.
  const { t } = useTranslation();
  const activityIdsKey = (activityIds || []).join(',');
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [activeSection, setActiveSection] = useState('document');
  const [hasPublicStatement, setHasPublicStatement] = useState(false);
  const [hasEndSignature, setHasEndSignature] = useState(false);

  // Refs for auto-save debounce — must not trigger on initial load
  const saveTimerRef = useRef(null);
  const initialLoadDone = useRef(false);

  // Graded outcome measures — org master switch (Organisation > Settings >
  // Reports card), read-only here. It gates whether the per-activity toggle
  // below can be used at all, but is never written by this tab.
  const [orgMasterGraded, setOrgMasterGraded] = useState(false);

  // Graded outcome measures — per-activity override, persisted on
  // sReportCustomisation (keyed by activityId), independent of org overrides.
  // null = no override for this activity (falls back to orgMasterGraded).
  const [activityGraded, setActivityGraded] = useState(null);
  const [activityGradedSaving, setActivityGradedSaving] = useState(false);

  useEffect(() => {
    setDefaults(PLATFORM_DEFAULTS);
    setOverrides({});
    setSavedAt(null);
    initialLoadDone.current = false;
    const singleActivityId =
      activityIds && activityIds.length === 1 ? activityIds[0] : null;
    const params = singleActivityId
      ? { activityIds: singleActivityId }
      : orgId
        ? { orgId }
        : {};
    reportBuilderGet('preferences', params)
      .then((j) => {
        if (j?.ok) {
          setDefaults(deepMerge(PLATFORM_DEFAULTS, j.defaults || {}));
          const loaded = j.orgOverrides || {};
          setOverrides(loaded);
          setHasPublicStatement(
            !!(loaded.publicStatement || loaded.publicStatementTitle)
          );
          setHasEndSignature(!!loaded.endSignatureUrl);
        }
      })
      .catch(() => {
        // API not yet implemented — PLATFORM_DEFAULTS already set above
      })
      .finally(() => {
        // Mark load done after the setOverrides re-render has committed
        requestAnimationFrame(() => {
          initialLoadDone.current = true;
        });
      });
  }, [orgId, activityIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The organisation's master graded-outcomes default (set on the
  // Organisation > Settings > Reports card) is always org-scoped, even when
  // the fetch above is scoped to a single activity's own overrides — fetch
  // it separately so it never gets confused with the activity's override.
  useEffect(() => {
    if (!orgId) {
      setOrgMasterGraded(false);
      return;
    }
    let cancelled = false;
    reportBuilderGet('preferences', { orgId })
      .then((j) => {
        if (cancelled) return;
        if (j?.ok) {
          setOrgMasterGraded(Boolean(j.orgOverrides?.outcomes?.useGraded));
        }
      })
      .catch(() => {
        if (!cancelled) setOrgMasterGraded(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Load the per-activity graded-outcome override whenever the selected
  // activity changes. No activityId (e.g. nothing selected yet) means no
  // per-activity persistence is possible — falls back to session-only.
  useEffect(() => {
    if (!activityId) {
      setActivityGraded(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/project/${activityId}/report-customisation`)
      .then(({ data }) => {
        if (cancelled) return;
        const val = data?.customisation?.preferences?.outcomes?.useGraded;
        setActivityGraded(
          val === undefined || val === null ? null : Boolean(val)
        );
      })
      .catch(() => {
        if (!cancelled) setActivityGraded(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  const handleToggleActivityGraded = useCallback(
    (next) => {
      if (!activityId) return;
      setActivityGraded(next);
      setActivityGradedSaving(true);
      axios
        .patch(`/project/${activityId}/report-customisation`, {
          preferences: { outcomes: { useGraded: next } },
        })
        .catch(() => {})
        .finally(() => setActivityGradedSaving(false));
    },
    [activityId]
  );

  const handleResetActivityGraded = useCallback(() => {
    if (!activityId) return;
    setActivityGraded(null);
    setActivityGradedSaving(true);
    axios
      .patch(`/project/${activityId}/report-customisation`, {
        preferences: { outcomes: { useGraded: null } },
      })
      .catch(() => {})
      .finally(() => setActivityGradedSaving(false));
  }, [activityId]);

  // Notify parent whenever the merged prefs change
  useEffect(() => {
    if (onPrefsChange) onPrefsChange(deepMerge(defaults, overrides));
  }, [defaults, overrides]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save: debounced 800ms after any overrides change; skips initial load.
  // Whenever any activities are selected (1 or more) activityIds is sent so
  // the backend can decide: exactly 1 → save against that activity; 2+ →
  // no-op (multi-activity preference saving isn't supported yet) rather than
  // falling through to an org-level write.
  useEffect(() => {
    const hasActivities = Boolean(activityIds && activityIds.length);
    if (!initialLoadDone.current || (!orgId && !hasActivities)) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const params = hasActivities
          ? { activityIds: activityIds.join(',') }
          : { orgId };
        const j = await reportBuilderPut('preferences', params, overrides);
        if (j?.ok) setSavedAt(new Date());
      } catch (e) {
        // silent — user can still see the preview; retry on next change
      } finally {
        setSaving(false);
      }
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [overrides]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((path, value) => {
    setOverrides((prev) => setPath(prev, path, value));
    setSavedAt(null);
  }, []);

  const handleClear = useCallback((path) => {
    setOverrides((prev) => deletePath(prev, path));
    setSavedAt(null);
  }, []);

  const coverColour = deepMerge(defaults, overrides)?.coverImageBackground
    ?.colour;

  const fp = {
    defaults,
    overrides,
    onChange: handleChange,
    onClear: handleClear,
  };

  return (
    <div className="pref">
      {/* Page header */}
      <div className="pref__header">
        <h2 className="pref__heading">
          {orgName || t('common.takso_platform')}
        </h2>
        <p className="pref__sub">
          <Trans
            i18nKey="report.builder.preferences.org_settings_hint"
            components={{
              strong: <strong className="pref__custom-label" />,
              em: <em />,
            }}
            values={{ org: orgName || 'Takso' }}
          />
        </p>
      </div>

      {/* Section tabs */}
      <div className="pref__tabs">
        {[
          ['document', t('report.builder.preferences.document')],
          ['charts', t('report.builder.charts')],
          ['colours', t('report.builder.preferences.colours')],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`pref__tab${
              activeSection === key ? ' pref__tab--active' : ''
            }`}
            onClick={() => setActiveSection(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Document ── */}
      {activeSection === 'document' && (
        <div className="pref__section">
          <SectionHeading>{t('typeface')}</SectionHeading>
          <SelectField
            label={t('report.builder.preferences.font_family')}
            path="document.fontFamily"
            options={FONTS}
            {...fp}
          />
          <SelectField
            label={t('report.builder.preferences.body_font_size')}
            hint={t('report.builder.preferences.body_font_size_hint')}
            path="document.bodyFontSize"
            options={SIZES}
            {...fp}
          />
          <SelectField
            label={t('report.builder.preferences.chart_title_style')}
            hint={t('report.builder.preferences.chart_title_style_hint')}
            path="document.chartTitleStyle"
            options={HEADING_STYLES}
            {...fp}
          />

          <SectionHeading>
            {t('report.builder.preferences.cover_page')}
          </SectionHeading>
          <ToggleRow
            label={t('report.builder.preferences.cover_page')}
            hint={t('report.builder.preferences.cover_page_hint')}
            path="document.cover.enabled"
            {...fp}
          />
          <TextField
            label={t('report.builder.preferences.report_header')}
            hint={t('report.builder.preferences.report_header_hint')}
            path="document.cover.tagline"
            {...fp}
          />
          <TextField
            label={t('formulary.organisation_name')}
            hint={t('report.builder.preferences.org_name_hint')}
            path="document.cover.orgName"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.corporate_statement')}
            hint={t('report.builder.preferences.corporate_statement_hint')}
            path="document.sections.corporateStatement"
            disabled={!hasPublicStatement}
            disabledNote={t(
              'report.builder.preferences.corporate_statement_disabled_hint'
            )}
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.end_signature')}
            hint={t('report.builder.preferences.end_signature_hint')}
            path="document.sections.endSignature"
            disabled={!hasEndSignature}
            disabledNote={t(
              'report.builder.preferences.end_signature_disabled_hint'
            )}
            {...fp}
          />

          <SectionHeading>
            <Trans i18nKey="report.builder.preferences.header_and_footer" />
          </SectionHeading>
          <ToggleRow
            label={t('report.builder.preferences.include_header')}
            hint={t('report.builder.preferences.include_header_hint')}
            path="document.header.enabled"
            {...fp}
          />
          <TextField
            label={t('report.builder.preferences.left_header_text')}
            path="document.header.leftText"
            {...fp}
          />
          <TextField
            label={t('right_header_text')}
            path="document.header.rightText"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.include_footer')}
            hint={t('report.builder.preferences.include_footer_hint')}
            path="document.footer.enabled"
            {...fp}
          />
          <TextField
            label={t('report.builder.preferences.footer_text')}
            path="document.footer.centreText"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.show_page_numbers')}
            path="document.footer.showPageNumbers"
            {...fp}
          />

          <SectionHeading>
            {t('report.builder.preferences.report_sections')}
          </SectionHeading>
          <ToggleRow
            label={t('report.builder.preferences.table_of_contents')}
            path="document.sections.tableOfContents"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.executive_summary')}
            hint={t('report.builder.preferences.executive_summary_hint')}
            path="document.sections.executiveSummary"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.strategic_alignment')}
            hint={t('report.builder.preferences.strategic_alignment_hint')}
            path="document.sections.strategicAlignment"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.intended_impact')}
            hint={t('report.builder.preferences.intended_impact_hint')}
            path="document.sections.intendedImpact"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.table_of_contents.evaluation')}
            hint={t('report.builder.preferences.evaluation_hint')}
            path="document.sections.evaluation"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.conclusions')}
            hint={t('report.builder.preferences.conclusions_hint')}
            path="document.sections.conclusions"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.appendices')}
            hint={t('report.builder.preferences.evidence_ref_table')}
            path="document.sections.appendices"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.survey_responses_appendix')}
            hint={t(
              'report.builder.preferences.survey_responses_appendix_hint'
            )}
            path="document.sections.surveyResponsesAppendix"
            {...fp}
          />
          <ToggleRow
            label={t('report.builder.preferences.glossary')}
            path="document.sections.glossary"
            {...fp}
          />
        </div>
      )}

      {/* ── Charts ── */}
      {activeSection === 'charts' && (
        <div className="pref__section">
          <p className="pref__charts-intro">
            <Trans i18nKey="report.builder.preferences.charts" />
          </p>

          <SectionHeading>{t('common.activities')}</SectionHeading>

          <SectionHeading>{t('common.outcomes')}</SectionHeading>
          <div className="pref-row pref-row--toggle">
            <Toggle
              checked={Boolean(
                activityGraded !== null ? activityGraded : orgMasterGraded
              )}
              onChange={handleToggleActivityGraded}
              disabled={!activityId}
            />
            <div className="pref-row__body">
              <div className="pref-row__label">
                {t(
                  'report.builder.preferences.graded_outcome_measures.heading'
                )}
                {activityGradedSaving && (
                  <span className="pref-row__hint">
                    {' '}
                    ({t('common.saving').toLowerCase()}…)
                  </span>
                )}
              </div>
              {!activityId ? (
                <div className="pref-row__hint">
                  {t(
                    'report.builder.preferences.graded_outcome_measures.select_activity'
                  )}
                </div>
              ) : (
                <div className="pref-row__hint">
                  {t(
                    'report.builder.preferences.graded_outcome_measures.show_accessible_version'
                  )}
                  {activityGraded !== null && (
                    <div className="pref-row__hint pref-row__hint--disabled">
                      {t(
                        'report.builder.preferences.graded_outcome_measures.is_activity_own_setting',
                        {
                          onOrOff: orgMasterGraded
                            ? t('common.on').toLowerCase()
                            : t('common.off').toLowerCase(),
                        }
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {activityId && activityGraded !== null && (
              <button
                className="pref-row__reset"
                onClick={handleResetActivityGraded}
                title={t('report.builder.preferences.use_org_default')}
              >
                {t('actions.reset').toLowerCase()}
              </button>
            )}
            {activityId && (
              <div className="pref-row__status">
                {activityGraded !== null ? (
                  <span className="pref-row__status--custom">
                    {t('report.builder.preferences.activity_settings')}
                  </span>
                ) : (
                  <span className="pref-row__status--default">
                    {t('report.builder.preferences.org_default')}
                  </span>
                )}
              </div>
            )}
          </div>
          <ToggleRow
            label={t('report.builder.preferences.survey_stats_line')}
            hint={t('report.builder.preferences.survey_stats_line_hint')}
            path="charts.outcomes.useSurveyStats"
            {...fp}
          />

          <SectionHeading>{t('report.outputs')}</SectionHeading>

          <SectionHeading>{t('report.inputs')}</SectionHeading>
        </div>
      )}

      {/* ── Colours ── */}
      {activeSection === 'colours' && (
        <div className="pref__section">
          <SectionHeading>
            {t('report.builder.preferences.banner_and_spot_colour_label')}
          </SectionHeading>
          <p className="pref__hint" style={{ marginBottom: 12 }}>
            {t('report.builder.preferences.banner_colour_applied')}
          </p>

          <ToggleRow
            label={t('report.builder.preferences.banner_and_spot_colour_label')}
            path="bannerColour.enabled"
            {...fp}
          />

          {deepMerge(defaults, overrides)?.bannerColour?.enabled && (
            <div className="pref__row" style={{ marginTop: 10 }}>
              <label className="pref__label" style={{ flex: 1 }}>
                {t('report.builder.preferences.colour')}
                <span
                  className="pref__hint"
                  style={{ display: 'block', marginTop: 2 }}
                >
                  {t(
                    'report.builder.preferences.select_banner_and_spot_colour'
                  )}
                </span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="color"
                  value={
                    deepMerge(defaults, overrides)?.bannerColour?.colour ||
                    '#ffffff'
                  }
                  onChange={(e) =>
                    handleChange('bannerColour.colour', e.target.value)
                  }
                  style={{
                    width: 44,
                    height: 32,
                    padding: 2,
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: '#6b7280',
                    fontFamily: 'monospace',
                  }}
                >
                  {deepMerge(defaults, overrides)?.bannerColour?.colour ||
                    '#ffffff'}
                </span>
                <button
                  className="pref__reset-btn"
                  onClick={() => handleChange('bannerColour.colour', '#ffffff')}
                  title={t('report.builder.preferences.reset_to_white')}
                >
                  {t('actions.reset').toLowerCase()}
                </button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <SectionHeading>
              {t('report.builder.preferences.cover_image_background_colour')}
            </SectionHeading>
          </div>
          <p className="pref__hint" style={{ marginBottom: 12 }}>
            {t(
              'report.builder.preferences.letterbox_colour_behind_cover_photo'
            )}
          </p>
          <div className="pref__row">
            <label className="pref__label" style={{ flex: 1 }}>
              {t('report.builder.preferences.colour')}
              <span
                className="pref__hint"
                style={{ display: 'block', marginTop: 2 }}
              >
                {t(
                  'report.builder.preferences.cover_photo.default_description'
                )}
              </span>
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <input
                type="color"
                value={
                  coverColour === null ? '#ffffff' : coverColour || '#1f2937'
                }
                onChange={(e) =>
                  handleChange('coverImageBackground.colour', e.target.value)
                }
                title={
                  coverColour === null
                    ? t('report.builder.preferences.no_colour_set_pick_one')
                    : t('report.builder.preferences.pick_colour')
                }
                style={{
                  width: 44,
                  height: 32,
                  padding: 2,
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  cursor: 'pointer',
                  opacity: coverColour === null ? 0.4 : 1,
                }}
              />

              <span
                style={{
                  fontSize: 12,
                  color: '#6b7280',
                  fontFamily: 'monospace',
                }}
              >
                {coverColour === null
                  ? t('report.builder.preferences.no_colour')
                  : coverColour || '#1f2937'}
              </span>

              <button
                type="button"
                className="pref__reset-btn"
                onClick={() =>
                  handleChange('coverImageBackground.colour', null)
                }
                title={t(
                  'report.builder.preferences.remove_background_colour_overlay'
                )}
                style={
                  coverColour === null
                    ? { fontWeight: 600, color: '#111827' }
                    : undefined
                }
              >
                {t('report.builder.preferences.no_colour').toLowerCase()}
              </button>

              <button
                className="pref__reset-btn"
                onClick={() =>
                  handleChange('coverImageBackground.colour', '#1f2937')
                }
                title={t('actions.reset_to_default')}
              >
                {t('actions.reset').toLowerCase()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-save status */}
      <div className="pref__save-bar">
        {saving ? (
          <span className="pref__saved-at" style={{ color: '#9ca3af' }}>
            {t('common.saving')}…
          </span>
        ) : savedAt ? (
          <span className="pref__saved-at">
            ✓ {t('common.saved')} {savedAt.toLocaleTimeString()}
          </span>
        ) : null}
      </div>
    </div>
  );
}
